import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { CardLayoutComponent, CardSection } from '../shared/card-layout/card-layout.component';
import { Store } from '@ngrx/store';
import { globalMessages } from '../shared/global-messages';

@Component({
    selector: 'app-billing-information',
    imports: [CardLayoutComponent],
    template: `
        <app-card-layout
            (click)="isActive = !isActive"
            [isActive]="isActive"
            title="Billing information"
            [isLoading]="isDCBillingLoaded"
            [failed]="dcBillingFailed"
            [sections]="sections"
            [noDataMessage]="!isDCBillingLoaded && sections.length === 0 ? globalMessages.noDataAvailabelMsg : ''"
            (retry)="retryDCBilling()"
        ></app-card-layout>
    `,
})
export class BillingInformationComponent {
    isActive = false;
    @Input() isDCBillingLoaded = false;
    @Input() dcBillingFailed = false;
    @Input() isThirdParty = false;
    globalMessages = globalMessages;
    @Output() retry = new EventEmitter<void>();
    private store = inject(Store);
    policySignal = this.store.selectSignal((state: any) => state.policy);

    get billingSnapshot() {
        const policyState = this.policySignal();
        const policyNum = policyState.policyNumber;
        return policyState.data?.[policyNum]?.billingSnapshot;
    }

    get installmentPlanDescription() {
        const policyState = this.policySignal();
        const policyNum = policyState.policyNumber;
        return policyState.data?.[policyNum]?.payPlanSnapshot?.policyTermInfo?.installmentPlanDescription?.value;
    }

    get paymentInfo() {
        return this.billingSnapshot?.paymentType;
    }

    get paymentFrequency() {
        return this.billingSnapshot?.paymentInformation?.paymentFrequency;
    }

    private readonly NON_CARD_METHODS = ['PA', 'Paper Invoices', 'EFT'];

    get isCreditCard(): boolean {
        return !this.NON_CARD_METHODS.includes(this.paymentInfo?.collectionMethod?.value);
    }

    get cardCaptionHtml(): string {
        const info = this.paymentInfo;
        const masked = `${info?.cardType?.value ?? ''} **** ${info?.cardNumber?.value ?? ''}`.trim();
        const name = info?.accountName?.value ?? '';
        const rawExpiry = info?.expirationDate?.value ?? '';
        const parts = rawExpiry.split(/[-\/]/);
        const expiry = parts.length >= 2 ? `${parts[0]}/${parts[1]}` : rawExpiry;
        return `<div class="d-flex flex-column" style="gap:0;line-height:1.4"><span class="caption-bold">${masked}</span>${
            name ? `<span class="caption">${name}</span>` : ''
        }<span class="caption">Expires in ${expiry}</span></div>`;
    }
    get isEFT(): boolean {
        return this.paymentInfo?.collectionMethod?.value === 'EFT';
    }

    get eftCaptionHtml(): string {
        const info = this.paymentInfo;
        const accountType = info?.accountType?.value ?? '---';
        const masked = `${accountType} **** ${info?.bankAccountNumber?.value ?? ''}`.trim();
        const name =
            typeof info?.accountName?.value === 'object'
                ? info?.accountName?.value?.['#text'] ?? ''
                : info?.accountName?.value ?? '';
        const bankName = info?.bankName?.value ?? '';
        return `<div class="d-flex flex-column" style="gap:0;line-height:1.4"><span class="caption-bold">${masked}</span>${
            name ? `<span class="caption">${name}</span>` : ''
        }${bankName ? `<span class="caption">${bankName}</span>` : ''}</div>`;
    }

    get cardIconName(): string {
        const cardType = this.paymentInfo?.cardType?.value?.toLowerCase() ?? '';
        if (cardType.includes('master')) return 'mastercard';
        if (cardType.includes('visa')) return 'visa';
        if (cardType.includes('american')) return 'americanExpress';
        if (cardType.includes('discover')) return 'discover';
        return 'generic-card-icon';
    }

    get sections(): CardSection[] {
        if (!this.paymentInfo && !this.paymentFrequency) return [];

        const fields: any[] = [
            {
                caption: 'Billing type',
                value: this.paymentInfo?.collectionMethodDescription?.value,
            },
        ];

        if (this.installmentPlanDescription) {
            fields.push({
                caption: 'Payment Plan',
                value: this.installmentPlanDescription,
            });
        }

        if (this.isCreditCard && !this.isThirdParty) {
            fields.push({
                caption: '',
                captionHtml: this.cardCaptionHtml,
                valueIcon: this.cardIconName,
            });
        }
        if (this.isEFT && !this.isThirdParty) {
            fields.push({
                caption: '',
                captionHtml: this.eftCaptionHtml,
            });
        }

        fields.push({
            caption: 'Enrolled in auto-payments',
            value: this.paymentInfo?.enrolledInAutoPay?.value ?? 'No',
        });

        return [{ title: 'Payment method', icon: 'payments-icon', fields }];
    }

    retryDCBilling() {
        this.retry.emit();
    }
}
